import { Link } from "react-router-dom";
import { ArrowRight, Mail, Phone, MapPin } from "lucide-react";
import Footer from "@/components/Footer";

// Logos
const ASKISAAC_LOGO = "/logos/askisaac-b-monogram.svg";

export default function WelcomeLetterPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 py-4">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-900 p-1.5 flex items-center justify-center">
              <img src={ASKISAAC_LOGO} alt="AskIsaac" className="w-full h-full object-contain" />
            </div>
            <span className="text-xl font-bold text-slate-900">AskIsaac</span>
          </Link>
          <div className="h-8 px-3 py-1 bg-slate-900 rounded-lg flex items-center justify-center">
            <img src={ASKISAAC_LOGO} alt="AskIsaac" className="h-full w-auto object-contain" />
          </div>
        </div>
      </header>

      {/* Letter Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
          
          {/* Letter Header */}
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-8 py-10 text-center">
            <img src={ASKISAAC_LOGO} alt="AskIsaac" className="w-20 h-20 mx-auto mb-4 rounded-xl" />
            <h1 className="text-3xl font-bold text-white mb-2">Welcome to AskIsaac.io</h1>
            <p className="text-slate-400">Your Healthcare Facility Concierge</p>
          </div>
          
          {/* Letter Body */}
          <div className="px-8 py-10 lg:px-12">
            <p className="text-slate-500 text-sm mb-8">January 2026</p>
            
            <p className="text-slate-800 text-lg leading-relaxed mb-6">
              Dear Valued Hospital Partners,
            </p>
            
            <p className="text-slate-700 leading-relaxed mb-6">
              On behalf of the entire team at <strong>Sterile Mate LLC</strong>, I am excited to introduce you to 
              <strong> AskIsaac.io</strong> — a <strong>Global Orchestration Exchange</strong> that serves as your 
              facility&apos;s &ldquo;Safety Valve&rdquo; when GPO contracts fail to deliver. Built on 6 years of experience 
              in the Texas Medical Center, AskIsaac bridges the gap that costs healthcare facilities an average of 
              $500,000 in annual downtime.
            </p>
            
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">The Challenge We&apos;re Solving</h2>
            
            <p className="text-slate-700 leading-relaxed mb-6">
              In the healthcare supply chain, the phrase &ldquo;We are under a GPO contract&rdquo; has become the standard 
              explanation for canceled surgeries and equipment downtime. While Group Purchasing Organizations provide 
              necessary volume savings, they often leave facilities needing additional options when supply chains face 
              disruptions or when specialized equipment needs immediate attention.
            </p>
            
            <p className="text-slate-700 leading-relaxed mb-6">
              We&apos;ve heard your needs: <em>&ldquo;How can we expand our supplier network quickly?&rdquo;</em> 
              <em>&ldquo;How do I find a credentialed contractor for urgent repairs?&rdquo;</em> <em>&ldquo;The manual 
              3-quote process takes weeks — we need faster options.&rdquo;</em>
            </p>
            
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Our Solution: A Network Extension Platform</h2>
            
            <p className="text-slate-700 leading-relaxed mb-6">
              AskIsaac.io enhances your existing GPO relationships by connecting your facility to an extended network of 
              pre-vetted, credentialed suppliers and contractors. We complement your current contracts with a 
              <strong> digital network extension</strong> — expanding your options while maintaining full compliance.
            </p>
            
            <p className="text-slate-700 leading-relaxed mb-6">
              AskIsaac.io serves as your single point of contact for:
            </p>
            
            <ul className="list-none space-y-3 mb-8 ml-4">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-slate-700"><strong>The QuickBid Engine</strong> — Skip the manual 3-quote bottleneck by instantly identifying verified local partners who have the parts and credentials to act today.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-slate-700"><strong>Extended Supplier Network</strong> — Complement your GPO with verified secondary providers offering immediate inventory and competitive pricing.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-slate-700"><strong>Automated AAMI ST108 Compliance</strong> — We remove the administrative burden of going &ldquo;off-contract.&rdquo; AskIsaac manages supplier vetting and digital validation, ensuring every transaction meets regulatory standards.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-violet-500 rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-slate-700"><strong>Cost Rectification</strong> — We use data-driven validation to stop the over-speccing of infrastructure. Hospitals save over $1M on single-department water systems using software-backed validation.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-cyan-500 rounded-full mt-2 flex-shrink-0"></span>
                <span className="text-slate-700"><strong>Complete Work Order Management</strong> — 8-stage pipeline from intake to completion with contractor dispatch, real-time tracking, and digital sign-off workflows.</span>
              </li>
            </ul>
            
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Why "Isaac"?</h2>
            
            <p className="text-slate-700 leading-relaxed mb-6">
              The name represents our <strong>I.S.A.A.C. Framework</strong> — the pillars upon which this platform stands:
            </p>
            
            <div className="bg-slate-50 rounded-xl p-6 mb-8 border border-slate-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div><strong className="text-cyan-600">I</strong>ntelligent — Smart dispatch and automated workflows</div>
                <div><strong className="text-cyan-600">S</strong>ourcing — Unified procurement ecosystem</div>
                <div><strong className="text-cyan-600">A</strong>ssured — Warranty protection and compliance</div>
                <div><strong className="text-cyan-600">A</strong>ccessible — 24/7 platform availability</div>
                <div><strong className="text-cyan-600">C</strong>onnected — Real-time communication across all parties</div>
              </div>
            </div>
            
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Getting Started</h2>
            
            <p className="text-slate-700 leading-relaxed mb-6">
              We invite you to explore the platform at <a href="https://sterilemate.com" className="text-cyan-600 font-semibold hover:underline">AskIsaac.io</a>. 
              Your account has been provisioned, and our team is standing by to walk you through any questions you may have.
            </p>
            
            <p className="text-slate-700 leading-relaxed mb-6">
              This is more than a software platform — it's our promise to be a better partner. We're listening, we're 
              adapting, and we're committed to making your job easier so you can focus on what matters most: patient care.
            </p>
            
            <p className="text-slate-700 leading-relaxed mb-8">
              Thank you for your continued trust in Sterile Mate. We're honored to serve you.
            </p>
            
            {/* Signature */}
            <div className="border-t border-slate-200 pt-8 mt-8">
              <p className="text-slate-800 font-semibold mb-1">Warmest regards,</p>
              <p className="text-slate-900 text-xl font-bold mb-1">George Broughton III</p>
              <p className="text-slate-600 mb-6">President & CEO, Sterile Mate LLC</p>
              
              <div className="bg-slate-900 rounded-xl p-6 text-white">
                <div className="flex items-center gap-4 mb-4">
                  <img src={ASKISAAC_LOGO} alt="AskIsaac" className="w-12 h-12 rounded-lg" />
                  <div>
                    <p className="font-bold text-cyan-400">ASKISAAC</p>
                    <p className="text-slate-400 text-sm"></p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-slate-300">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-cyan-400" />
                    <span>800.988.3892</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-cyan-400" />
                    <span>Direct: 281.456.3458</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-cyan-400" />
                    <span>Mobile: 800.988.3892</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-cyan-400" />
                    <span>george.broughton@sterilemate.com</span>
                  </div>
                  <div className="flex items-center gap-2 sm:col-span-2">
                    <MapPin className="w-4 h-4 text-cyan-400" />
                    <span>396 W Greens Road, Suite 1139, Houston, TX 77067</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* CTA */}
          <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-8 py-8 text-center">
            <p className="text-white text-lg mb-4">Ready to experience the future of facility management?</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-slate-900 rounded-lg font-semibold hover:bg-slate-100 transition-colors"
            >
              Explore AskIsaac.io
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}
